import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Phone, MessageCircle, Menu, X, MapPin, Download } from "lucide-react";
import Logo from "./Logo";
import UserMenu from "./UserMenu";
import { track } from "../lib/track";
import { BRAND, NAV, POPULAR_TESTS } from "../mock/mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [brand, setBrand] = useState(BRAND);
  const [suggestData, setSuggestData] = useState(POPULAR_TESTS);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [b, tests] = await Promise.all([api.getBrand(BRAND), api.getTests({}, POPULAR_TESTS)]);
      if (b) setBrand({ ...BRAND, ...b });
      if (tests?.length) setSuggestData(tests);
    })();
  }, []);

  const suggestions = query
    ? suggestData.filter((t) => t.name.toLowerCase().includes(query.toLowerCase())).slice(0, 5)
    : suggestData.slice(0, 5);

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-100">
      {/* Top strip */}
      <div className="hidden lg:block bg-slate-50 border-b border-slate-100">
        <div className="container-x flex items-center justify-between text-xs py-1.5 text-slate-600">
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-dl-teal"/> Ranchi · Jamshedpur · Ghatshila</span>
            <span>{brand.hours}</span>
          </div>
          <div className="flex items-center gap-4">
            <a href={`tel:${brand.phone}`} onClick={()=>track("call_tap", { target: brand.phone })} className="inline-flex items-center gap-1.5 hover:text-dl-blue"><Phone className="h-3.5 w-3.5"/> {brand.phone}</a>
            <a href={`https://wa.me/${(brand.whatsapp||"").replace(/\D/g,"")}`} target="_blank" rel="noreferrer" onClick={()=>track("whatsapp_tap", { target: brand.whatsapp })} className="inline-flex items-center gap-1.5 hover:text-dl-teal"><MessageCircle className="h-3.5 w-3.5"/> WhatsApp</a>
            <Link to="/report" className="inline-flex items-center gap-1.5 hover:text-dl-purple"><Download className="h-3.5 w-3.5"/> Download Report</Link>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="container-x flex items-center gap-4 py-3">
        <Logo />

        {/* Search */}
        <div className="relative flex-1 hidden md:block max-w-2xl mx-auto">
          <div className="flex items-center rounded-full border border-slate-200 bg-white shadow-sm px-3 py-1.5 focus-within:border-dl-blue focus-within:ring-2 focus-within:ring-blue-100 transition">
            <Search className="h-4 w-4 text-slate-400"/>
            <Input
              value={query}
              onChange={(e)=>setQuery(e.target.value)}
              onFocus={()=>setFocused(true)}
              onBlur={()=>setTimeout(()=>setFocused(false), 180)}
              placeholder="Search 400+ tests, packages or health concerns…"
              className="border-0 shadow-none focus-visible:ring-0 h-8 text-sm px-2"
            />
            <Button size="sm" onClick={() => navigate("/tests") } className="h-8 rounded-full bg-dl-blue hover:bg-blue-700 text-white text-xs px-4">Search</Button>
          </div>
          {focused && (
            <div className="absolute left-0 right-0 mt-2 bg-white border border-slate-200 rounded-2xl shadow-lg overflow-hidden z-50">
              <div className="px-4 py-2 text-[11px] uppercase font-semibold text-slate-500 tracking-wider bg-slate-50">Popular right now</div>
              {suggestions.map((s) => (
                <Link key={s.id} to={`/tests/${s.id}`} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 border-b last:border-b-0 border-slate-100">
                  <div>
                    <div className="text-sm font-medium text-slate-800">{s.name}</div>
                    <div className="text-xs text-slate-500">{s.short}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-dl-blue">₹{s.price}</div>
                    <div className="text-[10px] text-slate-400 line-through">₹{s.mrp}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="hidden md:flex items-center gap-2">
          <Button variant="outline" className="border-slate-200 rounded-full h-9" onClick={()=>navigate('/home-sample-collection-ranchi')}>
            Home Collection
          </Button>
          <Button className="rounded-full h-9 bg-dl-teal hover:bg-teal-600 text-white" onClick={()=>navigate('/book-test')}>Book a Test</Button>
          <UserMenu />
        </div>

        <button onClick={()=>setOpen(!open)} className="md:hidden ml-auto p-2 rounded-lg hover:bg-slate-100" aria-label="Menu">
          {open ? <X className="h-5 w-5"/> : <Menu className="h-5 w-5"/>}
        </button>
        <div className="md:hidden"><UserMenu /></div>
      </div>

      {/* Nav row */}
      <nav className="hidden md:block border-t border-slate-100">
        <div className="container-x flex items-center gap-1 overflow-x-auto no-scrollbar">
          {NAV.map((n)=> (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({isActive}) => `whitespace-nowrap text-sm px-3.5 py-2.5 border-b-2 -mb-px transition ${isActive ? 'border-dl-blue text-dl-blue font-semibold' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
            >
              {n.label}
            </NavLink>
          ))}
          <div className="ml-auto text-xs text-slate-500 hidden lg:flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-green-500 pulse-dot"/> Home collection slots open</div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {open && (
        <div className="md:hidden border-t border-slate-100 bg-white">
          <div className="container-x py-3 space-y-1">
            <div className="flex items-center rounded-full border border-slate-200 px-3 py-1.5 mb-3">
              <Search className="h-4 w-4 text-slate-400"/>
              <Input value={query} onChange={(e)=>setQuery(e.target.value)} placeholder="Search tests…" className="border-0 shadow-none focus-visible:ring-0 h-8 text-sm"/>
            </div>
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={()=>setOpen(false)} className="block px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-50">{n.label}</Link>
            ))}
            <div className="grid grid-cols-2 gap-2 pt-2">
              <a href={`tel:${brand.phone}`} onClick={()=>track("call_tap", { target: brand.phone })} className="flex items-center justify-center gap-2 bg-dl-blue text-white rounded-full py-2.5 text-sm font-medium"><Phone className="h-4 w-4"/> Call</a>
              <a href={`https://wa.me/${(brand.whatsapp||"").replace(/\D/g,"")}`} target="_blank" rel="noreferrer" onClick={()=>track("whatsapp_tap", { target: brand.whatsapp })} className="flex items-center justify-center gap-2 bg-dl-teal text-white rounded-full py-2.5 text-sm font-medium"><MessageCircle className="h-4 w-4"/> WhatsApp</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
